import { getApp } from "firebase/app";
import { arrayRemove, arrayUnion, doc, getFirestore, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { deleteToken, getMessaging, getToken, isSupported } from "firebase/messaging";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAppSelector } from "store/hooks";
import { getUserEmail } from "store/slices/user";
import { startForegroundMessaging } from "./notificationService";
import { pushLogger } from "./pushLogger";

const VAPID_KEY = import.meta.env.VITE_FCM_VAPID_KEY as string | undefined;
const FCM_TOKEN_LS_KEY = "todas_fcm_token";
const SW_PATH = "/firebase-messaging-sw.js";
/** Po překročení pokračujeme bez čekání na "activated" — viz poznámka u čekací smyčky. */
const SW_ACTIVATION_TIMEOUT_MS = 10_000;

export type PushPermissionState = "default" | "granted" | "denied" | "loading" | "unsupported" | "unavailable";

export interface UsePushNotificationsReturn {
  permissionState: PushPermissionState;
  requestPermission: () => Promise<void>;
  declinePermission: () => Promise<void>;
  isSupported: boolean;
}

const checkSupported = () =>
  typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;

/**
 * Uloží FCM token přímo do Firestore bez nutnosti BE (GraphQL mutace).
 *
 * Kolekce: pushTokens/{userEmail}
 * Přístup je zabezpečen Firebase Security Rules – zápis povolí jen
 * autentizovaný uživatel pro svůj vlastní dokument.
 *
 * Cloud Functions pak tokeny čtou pro odesílání notifikací.
 */
const saveTokenToFirestore = async (email: string, token: string): Promise<void> => {
  try {
    const db = getFirestore(getApp());
    // merge: true + arrayUnion → přidá token do pole bez přepsání ostatních zařízení
    await setDoc(
      doc(db, "pushTokens", email),
      { tokens: arrayUnion(token), updatedAt: serverTimestamp() },
      { merge: true },
    );
  } catch (error) {
    // Firestore zatím nemusí být povolené – token je bezpečně v localStorage
    pushLogger.warn("saveTokenToFirestore: failed — token saved only to localStorage", error);
  }
};

const removeTokenFromFirestore = async (email: string, token: string): Promise<void> => {
  try {
    const db = getFirestore(getApp());
    await updateDoc(doc(db, "pushTokens", email), { tokens: arrayRemove(token) });
  } catch {
    // ignore – dokument nemusí existovat
  }
};

/**
 * Hook pro správu push notifikací.
 *
 * - Registruje service worker (firebase-messaging-sw.js)
 * - Získá FCM token (vyžaduje VITE_FCM_VAPID_KEY)
 * - Uloží token do Firestore přímým klientem (bez BE)
 * - Token taky v localStorage pro případné lokální odeslání
 */
export const usePushNotifications = (): UsePushNotificationsReturn => {
  const email = useAppSelector(getUserEmail) ?? "";
  const supported = checkSupported();
  const [permissionState, setPermissionState] = useState<PushPermissionState>("loading");
  /** Srovnání tokenu při už uděleném oprávnění se dělá jednou za mount. */
  const reconciledRef = useRef(false);

  useEffect(() => {
    pushLogger.log(`checkSupported: ${supported}`);
    if (!supported) {
      setPermissionState("unsupported");
      return;
    }
    setPermissionState(Notification.permission as PushPermissionState);
  }, [supported]);

  /**
   * Zaregistruje service worker, získá FCM token a uloží ho do localStorage
   * i do Firestore.
   *
   * `replaceExisting` rozlišuje dvě situace:
   * - `true` – uživatel právě prošel bannerem. Starý token smažeme, ať Firebase
   *   začne s čistým stavem a nevznikne broken push subscription.
   * - `false` – jen srovnáváme stav při mountu, oprávnění bylo uděleno dřív.
   *   Mazat token by tu znamenalo rotovat subscription při každém otevření appky.
   */
  const acquireAndStoreToken = useCallback(
    async (replaceExisting: boolean) => {
      if (!email) return;

      if (!VAPID_KEY) {
        pushLogger.warn("acquireAndStoreToken: VAPID_KEY missing — aborting");
        setPermissionState("unavailable");
        return;
      }

      try {
        // Registruj SW a počkej na jeho plnou aktivaci.
        // navigator.serviceWorker.ready resolves když active != null, ale active může být
        // ještě ve stavu "activating". Chrome push API vyžaduje stav "activated".
        pushLogger.log(`SW: registering ${SW_PATH}`);
        await navigator.serviceWorker.register(SW_PATH);
        const swRegistration = await navigator.serviceWorker.ready;
        pushLogger.log("SW: ready");

        // Počkej na plný stav "activated" pokud SW ještě aktivuje.
        // AIDEV-NOTE: stav "activating" tu opravdu nastává (ověřeno na stage v Chrome),
        // takže tohle čekání není zbytečné. Timeout je ale nutný — bez něj se promise
        // nikdy neresolvne, pokud worker do "activated" nedojde, a celý opt-in tiše
        // zamrzne bez chyby. Po vypršení pokračujeme; getToken si případně stěžuje sám. [TD-306]
        if (swRegistration.active && swRegistration.active.state !== "activated") {
          await new Promise<void>(resolve => {
            const activeWorker = swRegistration.active;
            if (!activeWorker) {
              resolve();
              return;
            }

            const finish = () => {
              clearTimeout(timeoutId);
              activeWorker.removeEventListener("statechange", handler);
              resolve();
            };

            const timeoutId = setTimeout(() => {
              pushLogger.warn(
                `SW: still "${activeWorker.state}" after ${SW_ACTIVATION_TIMEOUT_MS} ms — continuing anyway`,
              );
              finish();
            }, SW_ACTIVATION_TIMEOUT_MS);

            const handler = () => {
              if (swRegistration.active?.state === "activated") finish();
            };
            activeWorker.addEventListener("statechange", handler);
            if (activeWorker.state === "activated") finish();
          });
        }
        pushLogger.log("SW: fully activated");

        // AIDEV-NOTE: checkSupported() výše testuje jen Notification/serviceWorker/PushManager.
        // Firebase navíc vyžaduje IndexedDB a cookies — což odpadá např. v Safari Private
        // Browsing. Bez téhle kontroly getMessaging() vyhodí výjimku, kterou catch níže
        // interpretuje jako obecnou chybu a uživatel skončí ve stavu "default", jako by
        // se nic nestalo. [TD-306]
        if (!(await isSupported())) {
          pushLogger.warn("firebase/messaging isSupported() === false — IndexedDB/cookies nedostupné?");
          setPermissionState("unsupported");
          return;
        }

        const messaging = getMessaging(getApp());

        if (replaceExisting) {
          // Před getToken vymažeme případný starý/expirovaný token, aby Firebase
          // začal s čistým stavem a nevznikala broken push subscription.
          // Selže-li deleteToken (token neexistuje), pokračujeme dál.
          pushLogger.log("deleteToken: start");
          try {
            await deleteToken(messaging);
            pushLogger.log("deleteToken: success");
          } catch {
            pushLogger.log("deleteToken: skipped (no existing token)");
          }
        }

        pushLogger.log("getToken: start");
        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: swRegistration,
        });
        pushLogger.log(`getToken: success — token ${token.slice(0, 8)}…${token.slice(-4)}`);

        localStorage.setItem(FCM_TOKEN_LS_KEY, token);
        pushLogger.log("token saved to localStorage");

        pushLogger.log("saveTokenToFirestore: start");
        await saveTokenToFirestore(email, token);
        pushLogger.log("saveTokenToFirestore: success");

        // Spustí foreground listener ihned po získání tokenu.
        // AuthenticatedArea ho spustí při mountu jen pokud bylo oprávnění uděleno dříve –
        // pro případ prvního udělení oprávnění ho musíme spustit tady.
        pushLogger.log("startForegroundMessaging() called from acquireAndStoreToken");
        startForegroundMessaging();
      } catch (error) {
        const isServiceUnavailable =
          error instanceof Error && error.name === "AbortError" && error.message.toLowerCase().includes("push service");

        if (isServiceUnavailable) {
          // Prohlížeč nemá přístup k push service (Brave s vypnutým FCM, firemní síť, apod.)
          pushLogger.warn("error: push service unavailable (Brave/corporate network?)", error);
          setPermissionState("unavailable");
        } else {
          pushLogger.error("error: unexpected failure in acquireAndStoreToken", error);
        }
        setPermissionState(prev => (prev === "loading" ? "default" : prev));
      }
    },
    [email],
  );

  const requestPermission = useCallback(async () => {
    if (!supported || !email) return;

    pushLogger.log("requestPermission: called");

    try {
      const permission = await Notification.requestPermission();
      pushLogger.log(`Notification.requestPermission() → "${permission}"`);
      setPermissionState(permission as PushPermissionState);

      if (permission !== "granted") return;

      // Token si bereme rovnou tady, takže srovnávací efekt níž už nemá co dělat.
      reconciledRef.current = true;
      await acquireAndStoreToken(true);
    } catch (error) {
      pushLogger.error("error: Notification.requestPermission() failed", error);
      setPermissionState(prev => (prev === "loading" ? "default" : prev));
    }
  }, [acquireAndStoreToken, email, supported]);

  // AIDEV-NOTE: Bez tohohle efektu platí, že jakmile je oprávnění "granted" a token
  // chybí nebo je neplatný, aplikace ho už nikdy nezíská — banner se renderuje jen
  // dokud je oprávnění "default" (PushNotificationOptIn.tsx) a je jediné místo, které
  // volá requestPermission(). Nastane to dvěma cestami: uživatel povolí notifikace
  // mimo náš banner (Edge má "quiet notification requests" zapnuté defaultně a nabídne
  // zvoneček v adresním řádku), nebo FCM token zneplatní a dailyPushReminder ho vyřadí
  // z Firestore. V obou případech uživatel vypadá opted-in a nedostane nic.
  //
  // getToken() voláme i když nějaký token v localStorage je — právě ten může být ten
  // zneplatněný, Firebase vrátí aktuální a arrayUnion zápis je idempotentní. Kontrola
  // na prázdný localStorage by tenhle, ten závažnější případ, minula. [TD-306]
  useEffect(() => {
    if (permissionState !== "granted") return;
    if (!email || reconciledRef.current) return;

    reconciledRef.current = true;
    pushLogger.log("permission already granted — reconciling token");
    void acquireAndStoreToken(false);
  }, [acquireAndStoreToken, email, permissionState]);

  const declinePermission = useCallback(async () => {
    const token = localStorage.getItem(FCM_TOKEN_LS_KEY);
    try {
      const messaging = getMessaging(getApp());
      await deleteToken(messaging);
    } catch {
      // token neexistuje – OK
    }
    localStorage.removeItem(FCM_TOKEN_LS_KEY);
    if (email && token) await removeTokenFromFirestore(email, token);
    // Nastavíme na aktuální stav prohlížeče – uživatel klikl "Teď ne",
    // Notification.permission je stále "default" (nebyl skutečně zamítnut)
    setPermissionState(Notification.permission as PushPermissionState);
  }, [email]);

  return {
    permissionState,
    requestPermission,
    declinePermission,
    isSupported: supported,
  };
};
